import { MOBILE_COUNTRY_CODES } from '@/src/constants/visa/visaRequirements'
import { Container, OutsideClickHandler, Typography } from '@/src/ct-design/lib'
import { countryDataType } from '@/src/types'
import React, { useEffect, useMemo, useRef, useState } from 'react'

const CountryCodeList = ({
    onClick,
    hideList,
    searchPlaceholder = 'Search country code...',
    noResultsText = 'No countries found',
    currentIsdCode
}: {
    onClick: (key: string, countryData: countryDataType) => void
    hideList: () => void
    searchPlaceholder?: string
    noResultsText?: string
    currentIsdCode?: string
}) => {
    const [searchTerm, setSearchTerm] = useState<string>('')
    const searchInputRef = useRef<HTMLInputElement>(null)

    // Focus search input when component mounts
    useEffect(() => {
        if (searchInputRef.current) {
            setTimeout(() => {
                searchInputRef.current?.focus()
            }, 100)
        }
    }, [])

    // Filter countries based on search term
    const filteredCountries = useMemo(() => {
        if (!searchTerm.trim()) return Object.entries(MOBILE_COUNTRY_CODES)

        return Object.entries(MOBILE_COUNTRY_CODES).filter(
            ([key, countryData]) => {
                const { name, code } = countryData
                const search = searchTerm.toLowerCase()
                return (
                    name.toLowerCase().includes(search) ||
                    code.includes(searchTerm) ||
                    key.toLowerCase().includes(search)
                )
            }
        )
    }, [searchTerm])

    const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        setSearchTerm(e.target.value)
    }

    const handleCountryClick = (key: string, countryData: countryDataType) => {
        onClick(key, countryData)
        setSearchTerm('') // Clear search on selection
    }

    return (
        <OutsideClickHandler onOutsideClick={hideList}>
            <Container
                className='w-70p br-4 p-absolute bg-neutral-100 z-100 ba-solid bc-neutral-500'
                style={{
                    boxShadow: '0px 8px 24px rgba(26, 26, 26, 0.1)',
                    top: '72px',
                    height: '240px', // Increased height to accommodate search
                    display: 'flex',
                    flexDirection: 'column'
                }}
            >
                {/* Search Input */}
                <Container className='px-3 py-2' style={{ flexShrink: 0 }}>
                    <input
                        ref={searchInputRef}
                        type='text'
                        value={searchTerm}
                        onChange={handleSearchChange}
                        placeholder={searchPlaceholder}
                        className='w-100p br-4 px-3 py-2 ba-solid bc-line-300 fs-14 lh-20'
                        style={{
                            border: '1px solid #d1d5db',
                            borderRadius: '6px',
                            outline: 'none',
                            fontSize: '14px',
                            lineHeight: '20px'
                        }}
                        onClick={(e) => e.stopPropagation()}
                    />
                </Container>

                {/* Country List */}
                <Container
                    className='flex flex-column p-1 br-6 oy-scroll flex-1'
                    style={{ overflow: 'auto' }}
                >
                    {filteredCountries.length > 0 ? (
                        filteredCountries.map(([key, countryData]) => {
                            const isSelected =
                                currentIsdCode === countryData.code
                            const { name, code } = countryData
                            return (
                                <Container
                                    onClick={() =>
                                        handleCountryClick(key, countryData)
                                    }
                                    key={key}
                                    height='48px'
                                    className={`flex flex-row flex-middle px-6 py-3 c-pointer ${
                                        isSelected ? 'bg-secondary-500' : ''
                                    }`}
                                >
                                    <Typography
                                        variant='B2'
                                        color={
                                            isSelected ? 'neutral' : 'primary'
                                        }
                                    >
                                        {name}
                                    </Typography>
                                    <Typography
                                        variant='B2'
                                        className='ml-1'
                                        color={
                                            isSelected ? 'neutral' : 'primary'
                                        }
                                    >
                                        {`(+${code})`}
                                    </Typography>
                                </Container>
                            )
                        })
                    ) : (
                        <Container
                            height='48px'
                            className='flex flex-middle flex-center'
                        >
                            <Typography
                                variant='B2'
                                color='primary'
                                className='fs-italic'
                            >
                                {noResultsText}
                            </Typography>
                        </Container>
                    )}
                </Container>
            </Container>
        </OutsideClickHandler>
    )
}

export default CountryCodeList
